import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { apiClient } from '../../lib/apiClient';
import NoChurchSelected from '../../components/common/NoChurchSelected';

function Give({ user }) {
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const myChurches = await apiClient.getMyChurches(user.id);
        
        if (myChurches.length === 0) {
          localStorage.removeItem('selectedChurch');
          setError('Please join a church first');
          return;
        }

        const stored = localStorage.getItem('selectedChurch');
        let church;
        if (stored) {
          const storedChurch = JSON.parse(stored);
          const isValidChurch = myChurches.some(c => c.id === storedChurch.id);
          if (isValidChurch) {
            church = storedChurch;
          } else {
            localStorage.removeItem('selectedChurch');
            church = myChurches[0];
            localStorage.setItem('selectedChurch', JSON.stringify(church));
            window.dispatchEvent(new Event('churchSelected'));
          }
        } else {
          church = myChurches[0];
          localStorage.setItem('selectedChurch', JSON.stringify(church));
          window.dispatchEvent(new Event('churchSelected'));
        }

        setSelectedChurch(church);
      } catch (err) {
        setError(err.message);
      }
    };

    if (user) {
      loadData();
    }
  }, [user]);

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and one decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  };

  const handleSubmit = () => {
    const numAmount = parseFloat(amount);
    if (!amount || isNaN(numAmount) || numAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    setConfirmDialogOpen(true);
  };

  const handleConfirmPayment = async () => {
    setConfirmDialogOpen(false);
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const numAmount = parseFloat(amount);
      await apiClient.createPayment(user.id, selectedChurch.id, numAmount, 'cash');
      setSuccess(true);
      setAmount('');
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  if (error && error === 'Please join a church first') {
    return <NoChurchSelected user={user} />;
  }

  if (error && !selectedChurch) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          fontSize: { xs: '1.5rem', sm: '2rem' },
          fontWeight: 600,
          mb: 3,
          color: '#2C2C2C',
        }}
      >
        Give to {selectedChurch?.name}
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Payment recorded successfully! Thank you for your generosity.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper
        sx={{
          p: 4,
          maxWidth: '600px',
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {selectedChurch && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Church
            </Typography>
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {selectedChurch.name}
            </Typography>
          </Box>
        )}

        <TextField
          label="Amount"
          type="text"
          value={amount}
          onChange={handleAmountChange}
          placeholder="0.00"
          fullWidth
          InputProps={{
            startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>₹</Typography>,
          }}
          disabled={loading}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: { xs: '1rem', sm: '1.25rem' },
            },
          }}
        />

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !amount || parseFloat(amount) <= 0}
          fullWidth
          size="large"
          sx={{
            backgroundColor: '#d4af39',
            '&:hover': { backgroundColor: '#8B6F47' },
            py: 1.5,
            fontSize: { xs: '1rem', sm: '1.125rem' },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Record Payment'}
        </Button>

        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
          Payment will be recorded as cash
        </Typography>
      </Paper>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="confirm-payment-dialog-title"
        aria-describedby="confirm-payment-dialog-description"
      >
        <DialogTitle id="confirm-payment-dialog-title">
          Confirm Payment
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-payment-dialog-description">
            Are you sure you want to record a payment of ₹{amount} to {selectedChurch?.name}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPayment}
            variant="contained"
            sx={{
              backgroundColor: '#d4af39',
              '&:hover': { backgroundColor: '#8B6F47' },
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Give;

